// Option Chain only. European, no dividends. ACT/365 Tehran calendar dates.
export const finite = x => typeof x === 'number' && Number.isFinite(x);
const ok = (value, extra = {}) => ({status:'OK', value, ...extra});
const unavailable = reason => ({status:'UNAVAILABLE', value:null, reason});
const validType = t => t === 'call' || t === 'put';
export function tehranDateKey(asOf = new Date()) {
  if (!(asOf instanceof Date) || !Number.isFinite(asOf.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Tehran',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(asOf);
  const get = k => parts.find(p=>p.type===k).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
function jalaliToGregorian(jy, jm, jd) {
  jy = parseInt(jy);
  jm = parseInt(jm);
  jd = parseInt(jd);

  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;

  let days = (jy <= 979 ? 0 : 365 * 33) +
    365 * jy +
    Math.floor(jy / 33) * 8 +
    Math.floor(((jy % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);

  gy += 400 * Math.floor(days / 146097);
  days %= 146097;

  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }

  gy += 4 * Math.floor(days / 1461);
  days %= 1461;

  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }

  let gd = days + 1;
  const gMonthDays = [0, 31, (gy % 4 === 0 && (gy % 100 !== 0 || gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let gm = 0;
  for (let i = 1; i <= 12; i++) {
    if (gd <= gMonthDays[i]) {
      gm = i;
      break;
    }
    gd -= gMonthDays[i];
  }

  return [gy, gm - 1, gd];
}

function formatGregorianDate(gy, gm, gd) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${gy}/${pad(gm + 1)}/${pad(gd)}`;
}

// Helper: strip time component from a date (midnight)
function dateAtMidnight(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}


export function maturity(expiry, asOf = new Date()) {
  const today = tehranDateKey(asOf);
  if (typeof expiry !== 'string' || !today) return {status:'INVALID', days:null, T:null};
  const normalized = expiry.replace(/[۰-۹]/g,c=>String('۰۱۲۳۴۵۶۷۸۹'.indexOf(c))).replace(/[٠-٩]/g,c=>String('٠١٢٣٤٥٦٧٨٩'.indexOf(c)));
  const match = normalized.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (!match) return {status:'INVALID', days:null, T:null};
  const [y,m,d] = match.slice(1).map(Number);
  if (m<1 || m>12 || d<1 || d>31 || y<1300 || y>2200) return {status:'INVALID',days:null,T:null};
  let civil;
  if (y<1600) {
    civil = jalaliToGregorian(y,m,d);
    const dt = new Date(Date.UTC(...[civil[0],civil[1],civil[2]],12));
    const parts = new Intl.DateTimeFormat('en-u-ca-persian',{timeZone:'UTC',year:'numeric',month:'numeric',day:'numeric'}).formatToParts(dt);
    if (!['year','month','day'].every((k,i)=>Number(parts.find(p=>p.type===k).value)===[y,m,d][i])) return {status:'INVALID',days:null,T:null};
  } else {
    civil=[y,m-1,d];
    const dt=new Date(Date.UTC(...civil));
    if (dt.getUTCFullYear()!==y || dt.getUTCMonth()!==m-1 || dt.getUTCDate()!==d) return {status:'INVALID',days:null,T:null};
  }
  const [ty,tm,td]=today.split('-').map(Number);
  const signedDays=(Date.UTC(...civil)-Date.UTC(ty,tm-1,td))/86400000;
  return {status:signedDays<0?'EXPIRED':signedDays===0?'SAME_DAY':'FUTURE',days:Math.max(0,signedDays),signedDays,T:Math.max(0,signedDays)/365,expiryISO:new Date(Date.UTC(...civil)).toISOString().slice(0,10),asOfDate:today};
}
// Regularized incomplete gamma gives a stable normal CDF without a pricing dependency.
function normalCDF(x) {
  if (x===0) return 0.5;
  const y=x*x/2, pref=Math.exp(-y+0.5*Math.log(y)-0.5*Math.log(Math.PI));
  let q;
  if(y<1.5){let sum=2,term=2,ap=0.5;for(let i=0;i<200;i++){ap++;term*=y/ap;sum+=term;if(Math.abs(term)<Math.abs(sum)*1e-15)break;}q=1-pref*sum;}
  else {let b=y+0.5,c=1e300,d=1/b,h=d;for(let i=1;i<200;i++){const an=-i*(i-0.5);b+=2;d=an*d+b;if(Math.abs(d)<1e-300)d=1e-300;c=b+an/c;if(Math.abs(c)<1e-300)c=1e-300;d=1/d;const delta=d*c;h*=delta;if(Math.abs(delta-1)<1e-15)break;}q=pref*h;}
  return x>0?1-0.5*q:0.5*q;
}
function inputs({S,K,T,r,sigma,type}) {return [S,K,T,r,sigma].every(finite)&&S>0&&K>0&&T>=0&&sigma>=0&&validType(type);}
export function blackScholes(args) {
  if(!inputs(args)) return unavailable('INVALID_INPUT');
  const {S,K,T,r,sigma,type}=args, D=K*Math.exp(-r*T);
  if(!finite(D)) return unavailable('INVALID_DISCOUNT');
  if(T===0) return ok(Math.max(type==='call'?S-K:K-S,0));
  if(sigma===0) return ok(Math.max(type==='call'?S-D:D-S,0));
  const d1=(Math.log(S/K)+(r+sigma*sigma/2)*T)/(sigma*Math.sqrt(T)), d2=d1-sigma*Math.sqrt(T);
  const value=type==='call'?S*normalCDF(d1)-D*normalCDF(d2):D*normalCDF(-d2)-S*normalCDF(-d1);
  return finite(value)&&value>=-1e-10*Math.max(S,K)?ok(Math.max(0,value)):unavailable('NUMERIC_FAILURE');
}
export function greeks(args) {
  if(!inputs(args)||args.T===0||args.sigma===0) return unavailable('UNDEFINED_SENSITIVITY');
  const {S,K,T,r,sigma,type}=args, root=Math.sqrt(T), D=K*Math.exp(-r*T);
  const d1=(Math.log(S/K)+(r+sigma*sigma/2)*T)/(sigma*root), d2=d1-sigma*root, pdf=Math.exp(-d1*d1/2)/Math.sqrt(2*Math.PI);
  const values={delta:normalCDF(d1)-(type==='put'?1:0),gamma:pdf/(S*sigma*root),vega:S*pdf*root/100,theta:(-S*pdf*sigma/(2*root)+(type==='call'?-r*D*normalCDF(d2):r*D*normalCDF(-d2)))/365,rho:(type==='call'?D*T*normalCDF(d2):-D*T*normalCDF(-d2))/100};
  return Object.values(values).every(finite)?ok(values):unavailable('NUMERIC_FAILURE');
}
export function impliedVolatility({price,S,K,T,r,type}) {
  if(!finite(price)||price<0||!inputs({S,K,T,r,sigma:0,type})||T<=0) return unavailable('INVALID_INPUT_OR_MATURITY');
  const D=K*Math.exp(-r*T),lower=Math.max(type==='call'?S-D:D-S,0),upper=type==='call'?S:D;
  if(!finite(D)||price<=lower||price>=upper) return unavailable('OUTSIDE_FINITE_IV_BOUNDS');
  let lo=0,hi=5;
  if(blackScholes({S,K,T,r,type,sigma:hi}).value<=price) return unavailable('ROOT_OUTSIDE_SOLVER_RANGE');
  const tolerance=Math.max(1e-8,Math.max(S,K,price)*1e-9);
  for(let i=0;i<120;i++) {
    const sigma=(lo+hi)/2, calculated=blackScholes({S,K,T,r,type,sigma}).value;
    if(!finite(calculated)) return unavailable('NUMERIC_FAILURE');
    const residual=calculated-price;
    if(Math.abs(residual)<=tolerance && hi-lo<=1e-10 && sigma>0 && sigma<5) return ok(sigma,{residual,iterations:i+1,lower,upper});
    if(residual>0)hi=sigma;else lo=sigma;
  }
  return unavailable('NO_CONVERGENCE');
}
// Reference-state Greeks: vega is per volatility percentage point, theta per day.
// Requires independently synchronized, fully dated observations; clock-only times are invalid.
export function taylorEstimate({reference,current}={}) {
  const dated = x => typeof x==='string' && /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(x) && finite(Date.parse(x));
  if(!reference||!current||reference.synchronized!==true||current.synchronized!==true||!dated(reference.timestamp)||!dated(current.timestamp)) return unavailable('MISSING_SYNCHRONIZED_DATED_REFERENCE');
  const elapsed=(Date.parse(current.timestamp)-Date.parse(reference.timestamp))/86400000;
  if(elapsed<0 || !finite(reference.price) || reference.price<0 || !finite(current.S) || current.S<=0 || !finite(current.sigma)||current.sigma<0 || current.K!==reference.K || current.type!==reference.type || current.r!==reference.r || !finite(current.T) || Math.abs(current.T-(reference.T-elapsed/365))>1e-8 || current.T<=0) return unavailable('INCONSISTENT_REFERENCE_STATE');
  const priced=blackScholes(reference);
  if(priced.status!=='OK'||Math.abs(priced.value-reference.price)>Math.max(1e-8,Math.max(reference.S,reference.K,reference.price)*1e-9)) return unavailable('INCONSISTENT_REFERENCE_PRICE');
  const g=greeks(reference);if(g.status!=='OK')return g;
  const ds=current.S-reference.S, dSigma=current.sigma-reference.sigma;
  const value=reference.price+g.value.delta*ds+0.5*g.value.gamma*ds*ds+g.value.vega*(100*dSigma)+g.value.theta*elapsed;
  return finite(value)&&value>=0?ok(value,{elapsedDays:elapsed}):unavailable('INVALID_ESTIMATE');
}
export function calculateContractMetrics(opt,S,asOf=new Date()) {
  const K=opt.strike_price,P=opt.price,type=opt.type,expiry=maturity(opt.maturity_date,asOf);
  const valid=finite(S)&&S>0&&finite(K)&&K>0&&validType(type), priceValid=finite(P)&&P>=0;
  const intrinsic=valid?Math.max(type==='call'?S-K:K-S,0):null;
  const timeValue=intrinsic!==null&&priceValid?P-intrinsic:null;
  const status=!valid?'UNKNOWN':S===K?'ATM':(type==='call'?S>K:S<K)?'ITM':'OTM';
  const args={S,K,T:expiry.T,r:0.30,sigma:0.40,type};
  const eligible=expiry.status==='FUTURE';
  const bs=eligible?blackScholes(args):unavailable('MATURITY_'+expiry.status);
  const iv=eligible?impliedVolatility({...args,price:P}):unavailable('MATURITY_'+expiry.status);
  const sensitivities=iv.status==='OK'?greeks({...args,sigma:iv.value}):unavailable(iv.reason);
  const breakEven=valid&&priceValid?K+(type==='call'?P:-P):null;
  return {daysLeft:expiry.days,expiry,status,bsValue:bs.value,bs,iv,greeks:sensitivities,intrinsic,timeValue,qualityFlags:timeValue!==null&&timeValue<0?['NEGATIVE_TIME_VALUE']:[],bubble:priceValid&&bs.value>0?(P-bs.value)/bs.value*100:null,diffWithStock:breakEven!==null?(breakEven-S)/S*100:null,liveFairValue:null,liveFairValueRaw:null,fairValueDiffPercent:null,taylor:taylorEstimate(),signalStatus:'سیگنال غیرفعال'};
}
